import React, { useState } from "react";
import "./DeleteAccount.css";
import { useTranslation, Trans } from "react-i18next";

const APP_NAME = "OPENlift";
const DEVELOPER_NAME = "Unitech Engineering";

/**
 * Email de contact affiché sur la page
 * À remplacer par l’adresse officielle de support utilisée pour les demandes RGPD / suppression.
 */
const SUPPORT_EMAIL = "contact@unitech-engineering.fr";

/**
 * Si tu as une API/backend pour recevoir les demandes, mets l’URL ici.
 * Si laissé vide, le formulaire ouvrira un email pré-rempli vers SUPPORT_EMAIL.
 */
const DELETE_REQUEST_ENDPOINT = "";

function buildMailtoLink(payload, t, policy) {
  const subject = t("deleteAccount.mailto.subject", {
    appName: APP_NAME,
  });

  const body = t("deleteAccount.mailto.body", {
    appName: APP_NAME,
    prenom: payload.prenom,
    nom: payload.nom,
    email: payload.email,
    telephone:
      payload.telephone || t("deleteAccount.mailto.fallbacks.notProvided"),
    entreprise:
      payload.entreprise || t("deleteAccount.mailto.fallbacks.notProvided"),
    identifiant:
      payload.identifiant || t("deleteAccount.mailto.fallbacks.notProvided"),
    typeDemande: payload.typeDemande,
    message: payload.message || t("deleteAccount.mailto.fallbacks.none"),
    processingDelay: policy.processingDelay,
  });

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

export default function DeleteAccount() {
  const { t } = useTranslation();

  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const policy = {
    identityVerificationDelay: t(
      "deleteAccount.policy.values.identityVerificationDelay"
    ),
    processingDelay: t("deleteAccount.policy.values.processingDelay"),
    backupRetention: t("deleteAccount.policy.values.backupRetention"),
    legalRetention: t("deleteAccount.policy.values.legalRetention"),
  };

  const deletedData = [
    t("deleteAccount.deletedData.items.0", { appName: APP_NAME }),
    t("deleteAccount.deletedData.items.1"),
    t("deleteAccount.deletedData.items.2"),
    t("deleteAccount.deletedData.items.3"),
    t("deleteAccount.deletedData.items.4"),
  ];

  const retainedData = [
    {
      title: t("deleteAccount.retainedData.items.0.title"),
      description: t("deleteAccount.retainedData.items.0.description"),
      duration: policy.backupRetention,
    },
    {
      title: t("deleteAccount.retainedData.items.1.title"),
      description: t("deleteAccount.retainedData.items.1.description"),
      duration: policy.legalRetention,
    },
    {
      title: t("deleteAccount.retainedData.items.2.title"),
      description: t("deleteAccount.retainedData.items.2.description"),
      duration: policy.legalRetention,
    },
  ];

  const requestSteps = [
    t("deleteAccount.requestSteps.0", { supportEmail: SUPPORT_EMAIL }),
    t("deleteAccount.requestSteps.1", { appName: APP_NAME }),
    t("deleteAccount.requestSteps.2", {
      identityVerificationDelay: policy.identityVerificationDelay,
    }),
    t("deleteAccount.requestSteps.3", {
      processingDelay: policy.processingDelay,
    }),
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSuccessMessage("");

    const formData = new FormData(e.currentTarget);
    const rawPayload = Object.fromEntries(formData.entries());

    const payload = {
      prenom: rawPayload.prenom?.trim() || "",
      nom: rawPayload.nom?.trim() || "",
      email: rawPayload.email?.trim() || "",
      telephone: rawPayload.telephone?.trim() || "",
      entreprise: rawPayload.entreprise?.trim() || "",
      identifiant: rawPayload.identifiant?.trim() || "",
      typeDemande: rawPayload.typeDemande?.trim() || "",
      message: rawPayload.message?.trim() || "",
      confirmation: formData.get("confirmation") === "on",
    };

    const nextErrors = {};

    if (!payload.prenom) {
      nextErrors.prenom = t("deleteAccount.validation.required");
    }

    if (!payload.nom) {
      nextErrors.nom = t("deleteAccount.validation.required");
    }

    if (!payload.email) {
      nextErrors.email = t("deleteAccount.validation.required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      nextErrors.email = t("deleteAccount.validation.invalidEmail");
    }

    if (!payload.typeDemande) {
      nextErrors.typeDemande = t("deleteAccount.validation.selectRequestType");
    }

    if (!payload.confirmation) {
      nextErrors.confirmation = t("deleteAccount.validation.confirmation");
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    try {
      setSending(true);

      if (DELETE_REQUEST_ENDPOINT) {
        const response = await fetch(DELETE_REQUEST_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            appName: APP_NAME,
            developer: DEVELOPER_NAME,
            ...payload,
          }),
        });

        if (!response.ok) {
          throw new Error(t("deleteAccount.submit.backendError"));
        }

        setSuccessMessage(t("deleteAccount.alert_success"));
      } else {
        const mailtoLink = buildMailtoLink(payload, t, policy);
        window.location.href = mailtoLink;

        setSuccessMessage(
          t("deleteAccount.form.submit.success_mailto", {
            supportEmail: SUPPORT_EMAIL,
          })
        );
      }

      e.currentTarget.reset();
      setErrors({});
    } catch (error) {
      setSubmitError(t("deleteAccount.submit_error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <main
      className="page page-delete-account"
      aria-labelledby="delete-account-title"
    >
      {/* ===== Hero ===== */}
      <section className="delete-account-hero" role="region">
        <div className="delete-account-container">
          <header className="delete-account-hero-header">
            <p className="delete-account-eyebrow">
              {APP_NAME} • {DEVELOPER_NAME}
            </p>

            <h1 id="delete-account-title" className="delete-account-title">
              {t("deleteAccount.hero.title")}
            </h1>

            <p className="delete-account-subtitle">
              <Trans
                i18nKey="deleteAccount.hero.subtitle_html"
                values={{
                  developerName: DEVELOPER_NAME,
                  appName: APP_NAME,
                }}
                components={{
                  strong: <strong />,
                }}
              />
            </p>
          </header>
        </div>
      </section>

      {/* ===== Infos principales ===== */}
      <section
        className="delete-account-info"
        aria-label={t("deleteAccount.cards.aria")}
      >
        <div className="delete-account-container">
          <div className="delete-account-grid">
            <article className="delete-card" aria-labelledby="procedure-title">
              <h2 id="procedure-title" className="delete-card-title">
                {t("deleteAccount.cards.procedure.title")}
              </h2>

              <p className="delete-card-text">
                <Trans
                  i18nKey="deleteAccount.cards.procedure.text_html"
                  values={{
                    appName: APP_NAME,
                    supportEmail: SUPPORT_EMAIL,
                  }}
                  components={{
                    strong: <strong />,
                    emailLink: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>,
                  }}
                />
              </p>

              <ol className="steps-list">
                {requestSteps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>

              <p className="delete-card-note">
                {t("deleteAccount.cards.procedure.note")}
              </p>
            </article>

            <article className="delete-card" aria-labelledby="deleted-data-title">
              <h2 id="deleted-data-title" className="delete-card-title">
                {t("deleteAccount.deletedData.title")}
              </h2>

              <p className="delete-card-text">
                {t("deleteAccount.deletedData.text")}
              </p>

              <ul className="policy-list">
                {deletedData.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </article>

            <article className="delete-card" aria-labelledby="retained-data-title">
              <h2 id="retained-data-title" className="delete-card-title">
                {t("deleteAccount.retainedData.title")}
              </h2>

              <p className="delete-card-text">
                {t("deleteAccount.retainedData.text")}
              </p>

              <ul className="retention-list">
                {retainedData.map((item, index) => (
                  <li key={index} className="retention-item">
                    <span className="retention-item-title">{item.title}</span>
                    <span className="retention-item-text">{item.description}</span>
                    <span className="retention-item-duration">
                      {t("deleteAccount.retainedData.durationLabel", {
                        duration: item.duration,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ===== Bloc délais ===== */}
      <section
        className="delete-account-policy"
        aria-labelledby="policy-title"
      >
        <div className="delete-account-container">
          <div className="policy-box">
            <h2 id="policy-title" className="policy-title">
              {t("deleteAccount.policy.title")}
            </h2>

            <p className="policy-text">
              <Trans
                i18nKey="deleteAccount.policy.text_html"
                values={{
                  developerName: DEVELOPER_NAME,
                  processingDelay: policy.processingDelay,
                }}
                components={{
                  strong: <strong />,
                }}
              />
            </p>

            <ul className="policy-bullets">
              <li>
                {t("deleteAccount.policy.bullets.identity", {
                  identityVerificationDelay: policy.identityVerificationDelay,
                })}
              </li>
              <li>
                {t("deleteAccount.policy.bullets.processing", {
                  processingDelay: policy.processingDelay,
                })}
              </li>
              <li>
                {t("deleteAccount.policy.bullets.backup", {
                  backupRetention: policy.backupRetention,
                })}
              </li>
              <li>
                {t("deleteAccount.policy.bullets.legal", {
                  legalRetention: policy.legalRetention,
                })}
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== Formulaire ===== */}
      <section
        className="delete-account-form-section"
        aria-labelledby="delete-form-title"
      >
        <div className="delete-account-container">
          <header className="delete-form-header">
            <h2 id="delete-form-title" className="delete-form-title">
              {t("deleteAccount.form.title")}
            </h2>

            <p className="delete-form-deck">
              <Trans
                i18nKey="deleteAccount.form.deck_html"
                values={{
                  appName: APP_NAME,
                }}
                components={{
                  strong: <strong />,
                }}
              />
            </p>
          </header>

          <form className="delete-account-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="prenom">
                  {t("deleteAccount.form.fields.prenom.label")}
                </label>
                <input id="prenom" name="prenom" autoComplete="given-name" />
                {errors.prenom && <p className="field-error">{errors.prenom}</p>}
              </div>

              <div className="form-field">
                <label htmlFor="nom">
                  {t("deleteAccount.form.fields.nom.label")}
                </label>
                <input id="nom" name="nom" autoComplete="family-name" />
                {errors.nom && <p className="field-error">{errors.nom}</p>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="email">
                  {t("deleteAccount.form.fields.email.label", {
                    appName: APP_NAME,
                  })}
                </label>
                <input id="email" type="email" name="email" autoComplete="email" />
                {errors.email && <p className="field-error">{errors.email}</p>}
              </div>

              <div className="form-field">
                <label htmlFor="telephone">
                  {t("deleteAccount.form.fields.telephone.label")}
                </label>
                <input id="telephone" name="telephone" autoComplete="tel" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="entreprise">
                  {t("deleteAccount.form.fields.entreprise.label")}
                </label>
                <input
                  id="entreprise"
                  name="entreprise"
                  autoComplete="organization"
                />
              </div>

              <div className="form-field">
                <label htmlFor="identifiant">
                  {t("deleteAccount.form.fields.identifiant.label")}
                </label>
                <input
                  id="identifiant"
                  name="identifiant"
                  placeholder={t("deleteAccount.form.fields.identifiant.placeholder")}
                />
              </div>
            </div>

            <div className="form-row single">
              <div className="form-field">
                <label htmlFor="typeDemande">
                  {t("deleteAccount.form.fields.typeDemande.label")}
                </label>
                <select id="typeDemande" name="typeDemande" defaultValue="">
                  <option value="" disabled>
                    {t("deleteAccount.form.fields.typeDemande.placeholder")}
                  </option>
                  <option value="suppression-compte-complete">
                    {t(
                      "deleteAccount.form.fields.typeDemande.options.suppressionCompteComplete"
                    )}
                  </option>
                  <option value="suppression-compte-et-donnees">
                    {t(
                      "deleteAccount.form.fields.typeDemande.options.suppressionCompteEtDonnees"
                    )}
                  </option>
                  <option value="suppression-suite-inactivite">
                    {t(
                      "deleteAccount.form.fields.typeDemande.options.suppressionSuiteInactivite"
                    )}
                  </option>
                  <option value="autre">
                    {t("deleteAccount.form.fields.typeDemande.options.autre")}
                  </option>
                </select>
                {errors.typeDemande && (
                  <p className="field-error">{errors.typeDemande}</p>
                )}
              </div>
            </div>

            <div className="form-row single">
              <div className="form-field">
                <label htmlFor="message">
                  {t("deleteAccount.form.fields.message.label")}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  placeholder={t("deleteAccount.form.fields.message.placeholder")}
                />
              </div>
            </div>

            <div className="checkbox-field">
              <label className="checkbox">
                <input type="checkbox" id="confirmation" name="confirmation" />
                <span>
                  {t("deleteAccount.form.fields.confirmation.label")}
                </span>
              </label>
              {errors.confirmation && (
                <p className="field-error">{errors.confirmation}</p>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-solid" disabled={sending}>
                {sending
                  ? t("deleteAccount.form.submit.sending")
                  : t("deleteAccount.form.submit.label")}
              </button>

              {submitError && (
                <p className="submit-error" role="alert">
                  {submitError}
                </p>
              )}

              {successMessage && (
                <p className="submit-success" role="status">
                  {successMessage}
                </p>
              )}
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}